import React from 'react';
import { Paper, Box, Typography, Skeleton } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

/**
 * Gráfico de distribución de estados de servicios
 * Muestra un gráfico de dona con la distribución porcentual
 */
const DistribucionEstadosChart = ({ data, loading = false }) => {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400
        }}
      >
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="circular" width={250} height={250} sx={{ mt: 2, mx: 'auto' }} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="text.secondary">No hay datos disponibles</Typography>
      </Paper>
    );
  }

  // Formatear datos para el gráfico
  const chartData = data.map(item => ({
    name: item.nombre_estado,
    value: parseInt(item.cantidad) || 0,
    porcentaje: parseFloat(item.porcentaje) || 0,
    color: item.color
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
            {data.name}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
            Cantidad: <strong>{data.value}</strong>
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
            Porcentaje: <strong>{data.payload.porcentaje.toFixed(1)}%</strong>
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // No mostrar label si es menos del 5%

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '0.75rem', fontWeight: 600 }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        height: 400
      }}
    >
      {/* Header */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#1A237E',
          fontSize: '1.125rem',
          mb: 2
        }}
      >
        🔄 Distribución por Estados
      </Typography>

      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={CustomLabel}
            outerRadius={100}
            innerRadius={60}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={2}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            wrapperStyle={{ fontSize: '0.75rem' }}
            formatter={(value, entry) => {
              const item = chartData.find(d => d.name === value);
              return `${value} (${item?.value || 0})`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default DistribucionEstadosChart;
